import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import {
  Controller,
  FieldError,
  UseFormClearErrors,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form'
import { LuCirclePlus, LuInfo } from 'react-icons/lu'

import { useSettings } from '@/feature/organizations/api/useSettings'
import { useTelemetryStore } from '@/feature/telemetry/store/telemetry'
import { useIntl } from 'react-intl'
import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'

import { DatePicker, DatePickerTrigger } from '@/lib/core/components/DatePicker'
import { RadioButtons } from '@/lib/core/components/RadioButtons'
import { SelectField, SelectOption } from '@/lib/core/components/Select'
import { Input } from '@/lib/core/components/TextField/TextField.styles'
import { SHORT_DATE_FORMAT } from '@/lib/core/constants/constants'

import {
  isShareBlockedByOffline,
  OFFLINE_BLOCKED_ERROR,
  OFFLINE_BLOCKED_TOOLTIP,
  OFFLINE_EASED_NOTICE,
  resolveOfflineAfterMinutes,
  shouldNoticeEasedOffline,
} from '@/lib/features/Assets/assetOfflineStatus.utils'
import { Asset } from '@/lib/features/Assets/interfaces'
import {
  SelectAdorment,
  ShareFormState,
} from '@/lib/features/SmartShares/components/ShareAssetModal'
import {
  DELEGATION_BLOCKED_ERROR,
  DELEGATION_BLOCKED_TOOLTIP,
  getBlockedShareFlowReason,
  isAssetBlockedByShareClass,
} from '@/lib/features/SmartShares/components/ShareAssetModal/utils'
import {
  CellDuration,
  DurationTypeTd,
  Error,
  Remove,
  Td,
} from '@/lib/features/SmartShares/components/ShareAssetModal/components/AssetsTable'
import messages from '@/lib/features/SmartShares/components/ShareAssetModal/ShareAssetModal.messages'
import { REFERENCE_MAX_LENGTH } from '@/lib/features/SmartShares/constants'
import { Share } from '@/lib/features/SmartShares/interfaces'
import { useDateRangeStore } from '@/lib/features/SmartShares/store'

import styles from './ShareRow.module.css'

export type ShareRowProps = {
  items: Array<SelectOption>
  index: number
  isLoading?: boolean
  onAddNewAsset?: () => void
  onRemove?: () => void
  shareToExtend?: Share
  onwardShareToExtend?: Share
  rows: number
  uid?: string
}

export const ShareRow = ({
  shareToExtend,
  onwardShareToExtend,
  items,
  index,
  isLoading,
  onAddNewAsset,
  onRemove = () => null,
  rows,
}: ShareRowProps) => {
  const { formatMessage } = useIntl()
  const shareClass = useShareAssetModalStore((state) => state.shareClass)
  const { clearErrors, control, formState, setValue, watch, setError, getValues } =
    useFormContext<ShareFormState>()
  const currentValue = watch(`assets.${index}`)
  const organizationTo = watch('organizationTo')
  const selectedAssets = watch('assets')
  const defaultValue = useMemo(() => {
    if (onwardShareToExtend) {
      return items.find(({ value }) => onwardShareToExtend.assetId === value)
    }
    if (shareToExtend) {
      return items.find(({ value }) => shareToExtend.assetId === value)
    }
    return items.find(({ value }) => currentValue.asset === value)
  }, [items, currentValue])

  // Honours the organization's configured offline timeout, same as the map
  const { data: organizationSettings } = useSettings()
  const offlineAfterMinutes = resolveOfflineAfterMinutes(organizationSettings?.vehicleOfflineAfter)

  // Avoid subscribing to telemetry updates to prevent re-renders; read on demand using Zustand store getter
  const getAssetsTracking = () => useTelemetryStore.getState().tracking

  // Ensure telemetry websocket is active while this row is mounted, so that the
  // offline notices below reflect real tracking rather than an unpopulated store
  const incrementTelemetryListeners = useTelemetryStore((s) => s.incrementActiveListeners)
  const decrementTelemetryListeners = useTelemetryStore((s) => s.decrementActiveListeners)
  useEffect(() => {
    incrementTelemetryListeners()
    return () => decrementTelemetryListeners()
  }, [incrementTelemetryListeners, decrementTelemetryListeners])

  // Extending or passing on a delegation is not allowed in either direction. Derived once so the
  // dropdown tooltip and the row error below always say the same thing.
  const blockedFlowReason = useMemo(
    () => getBlockedShareFlowReason({ shareToExtend, onwardShareToExtend }),
    [shareToExtend, onwardShareToExtend],
  )

  const options = useMemo(() => {
    if (onwardShareToExtend) {
      return items
    }

    const values = getValues()
    const { organizationTo } = values

    const filtered = items.filter((item) => !selectedAssets?.find((sa) => sa.asset === item.value))

    return filtered.map((item) => {
      if (item.data && organizationTo) {
        const assetData = item.data.asset as Asset
        item.data.isOwnedFromOrganizationTo = organizationTo === assetData?.owner.id
      }
      if (item.data) {
        const asset = item.data.asset as Asset
        const tracking = getAssetsTracking().get(item.data.id as string)

        // The share class is the most fundamental reason — a tracker is never delegable, whatever
        // its owner or tracking says. Offline blocks selection next, unless the asset's location is
        // intermittent. Ownership is more fundamental than offline, so it keeps its own copy.
        item.data.disabledReason = blockedFlowReason
          ? blockedFlowReason
          : isAssetBlockedByShareClass(asset, shareClass)
            ? DELEGATION_BLOCKED_TOOLTIP
            : !item.data.isOwnedFromOrganizationTo &&
                isShareBlockedByOffline(asset, tracking, offlineAfterMinutes)
              ? OFFLINE_BLOCKED_TOOLTIP
              : undefined
        item.data.notice = shouldNoticeEasedOffline(asset, tracking, offlineAfterMinutes)
          ? OFFLINE_EASED_NOTICE
          : undefined
      }
      return item
    })
  }, [
    items,
    selectedAssets,
    organizationTo,
    onwardShareToExtend,
    offlineAfterMinutes,
    shareClass,
    blockedFlowReason,
  ])

  const currentAssetData = useMemo(() => {
    const item = items.find(({ value }) => currentValue.asset === value)
    if (item?.data?.asset) {
      return item.data.asset as Asset
    }
    return null
  }, [currentValue.asset])

  const isCurrentAssetEasedOffline = useMemo(() => {
    const item = items.find(({ value }) => currentValue.asset === value)
    if (item?.data?.id == undefined) {
      return false
    }
    return shouldNoticeEasedOffline(
      item.data.asset as Asset,
      getAssetsTracking().get(item.data.id as string),
      offlineAfterMinutes,
    )
  }, [items, currentValue.asset, offlineAfterMinutes])

  // A delegation cannot be passed on at all, so that outranks everything. Failing that the
  // delegate flow takes vehicles only; otherwise offline blocks the share unless the asset's
  // location is intermittent. One effect owns this error key so the rules cannot fight.
  useEffect(() => {
    const item = items.find(({ value }) => currentValue.asset === value)
    if (item?.data?.id == undefined) return
    const asset = item.data.asset as Asset
    const tracking = getAssetsTracking().get(item.data.id as string)

    if (blockedFlowReason) {
      setError(`assets.${index}.asset`, {
        type: 'validate',
        message: blockedFlowReason,
      })
    } else if (isAssetBlockedByShareClass(asset, shareClass)) {
      setError(`assets.${index}.asset`, {
        type: 'validate',
        message: DELEGATION_BLOCKED_ERROR,
      })
    } else if (isShareBlockedByOffline(asset, tracking, offlineAfterMinutes)) {
      setError(`assets.${index}.asset`, {
        type: 'validate',
        message: OFFLINE_BLOCKED_ERROR,
      })
    } else {
      clearErrors(`assets.${index}.asset`)
    }
  }, [selectedAssets, currentValue.asset, offlineAfterMinutes, shareClass, blockedFlowReason])

  /** Same one-shot seeding as the onward prefill below, and loops the same way. */
  const prefilledShareId = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (options.length > 0 && shareToExtend) {
      if (prefilledShareId.current === shareToExtend.id) {
        return
      }
      prefilledShareId.current = shareToExtend.id

      setValue(`assets.${index}.asset`, shareToExtend.assetId)
      setValue(`assets.${index}.reference`, shareToExtend.reference)
      if (dayjs(shareToExtend?.dateFrom).isSame(shareToExtend?.dateTo, 'date')) {
        setValue(`assets.${index}.durationType`, 'instant')
      } else {
        setValue(`assets.${index}.durationType`, 'custom')
        setValue(`assets.${index}.date`, {
          to: shareToExtend.dateTo,
          from: shareToExtend.dateFrom,
        })
      }
    }
  }, [options.length, shareToExtend, index, setValue])

  useEffect(() => {
    if (currentAssetData) {
      setValue(`assets.${index}.ownerId`, currentAssetData.owner.id)
    }
  }, [currentAssetData])

  const { errors, isSubmitting } = formState

  const durationOptions = [
    {
      label: formatMessage(messages.durationCustom),
      value: 'custom',
    },
    {
      label: formatMessage(messages.durationInstant),
      value: 'instant',
    },
  ]

  /**
   * Prefilling an onward share is a one-shot: it seeds the row from the parent
   * share and must not run again. Keyed on the share id rather than on
   * `options`, whose identity churns, because each run writes a newly built
   * date object, which re-validates the form, which re-renders the row — an
   * endless loop that only the onward branch could reach.
   */
  const prefilledOnwardShareId = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (options.length > 0 && onwardShareToExtend) {
      if (prefilledOnwardShareId.current === onwardShareToExtend.id) {
        return
      }
      prefilledOnwardShareId.current = onwardShareToExtend.id

      setValue(`assets.${index}.asset`, onwardShareToExtend.assetId)
      setValue(`assets.${index}.reference`, onwardShareToExtend.reference)
      if (dayjs(onwardShareToExtend?.dateFrom).isSame(onwardShareToExtend?.dateTo, 'date')) {
        setValue(`assets.${index}.durationType`, 'instant')
      } else {
        setValue(`assets.${index}.durationType`, 'custom')
        // if the onward share start on the past
        // then we need to ensure it starts from today to share onward
        if (dayjs(onwardShareToExtend?.dateFrom).isBefore(dayjs().startOf('day'))) {
          setValue(`assets.${index}.date`, {
            to: onwardShareToExtend.dateTo,
            from: dayjs().startOf('day').toISOString(),
          })
        } else {
          setValue(`assets.${index}.date`, {
            to: onwardShareToExtend.dateTo,
            from: onwardShareToExtend.dateFrom,
          })
        }
      }
    }
  }, [options.length, onwardShareToExtend, index, setValue])

  const shouldDisabledRangeStart = () => {
    if (!shareToExtend) {
      return false
    }

    // If share has dateFrom of any time today or earlier, then it's active and dateFrom may not be changed
    return dayjs(shareToExtend.dateFrom) < dayjs().endOf('day')
  }

  const [durationType, setDurationType] = useState('')

  const [minDateRange, setMinDateRange] = useState<Date | undefined>()
  const [maxDateRange, setMaxDateRange] = useState<Date | undefined>()

  const { getDateRange, setDateRange } = useDateRangeStore()

  useEffect(() => {
    if (onwardShareToExtend && !maxDateRange) {
      const storedDateRange = getDateRange(onwardShareToExtend.id)
      if (storedDateRange.maxDate) {
        setMaxDateRange(new Date(storedDateRange.maxDate))
      } else {
        setMaxDateRange(new Date(onwardShareToExtend.dateTo))
        setDateRange(onwardShareToExtend.id, undefined, onwardShareToExtend.dateTo)
      }
    }
    if (onwardShareToExtend && !minDateRange) {
      const storedDateRange = getDateRange(onwardShareToExtend.id)
      if (storedDateRange.minDate) {
        setMinDateRange(new Date(storedDateRange.minDate))
      } else {
        if (dayjs(onwardShareToExtend.dateFrom).isBefore(dayjs().startOf('day'))) {
          setMinDateRange(new Date())
          setDateRange(onwardShareToExtend.id, dayjs().toISOString(), undefined)
        } else {
          setMinDateRange(new Date(onwardShareToExtend.dateFrom))
          setDateRange(onwardShareToExtend.id, onwardShareToExtend.dateFrom, undefined)
        }
      }
    }
  }, [onwardShareToExtend, getDateRange, setDateRange])

  useEffect(() => {
    if (onAddNewAsset) {
      setDurationType('instant')
    } else {
      if (shareToExtend) {
        if (dayjs(shareToExtend?.dateFrom).isSame(shareToExtend?.dateTo, 'date')) {
          setDurationType('instant')
        }
      } else if (onwardShareToExtend) {
        if (dayjs(onwardShareToExtend?.dateFrom).isSame(onwardShareToExtend?.dateTo, 'date')) {
          setDurationType('instant')
        }
      } else {
        setDurationType('custom')
      }
    }
  }, [shareToExtend, onwardShareToExtend])

  // Wait until the things fully setup to the onward share
  if (onwardShareToExtend && !minDateRange) {
    return null
  }

  const handleDurationTypeChange = ({
    value,
    field,
    index,
    shareToExtend,
    onwardShareToExtend,
    currentValue,
    setValue,
    clearErrors,
    setDurationType,
  }: {
    value: string
    field: { onChange: (value: string) => void }
    index: number
    shareToExtend?: Share
    onwardShareToExtend?: Share
    currentValue: { date?: { from?: string; to?: string } | null }
    setValue: UseFormSetValue<ShareFormState>
    clearErrors: UseFormClearErrors<ShareFormState>
    setDurationType: (value: string) => void
  }) => {
    field.onChange(value)
    setDurationType(value)

    if (value === 'instant') {
      setValue(`assets.${index}.date`, {
        from: dayjs().startOf('day').toISOString(),
        to: dayjs().endOf('day').toISOString(),
      })
    } else if (value === 'custom') {
      // Restore existing share dates based on share type
      if (shareToExtend) {
        setValue(`assets.${index}.date`, {
          from: shareToExtend.dateFrom,
          to: shareToExtend.dateTo,
        })
      } else if (onwardShareToExtend) {
        const fromDate = dayjs(onwardShareToExtend.dateFrom).isBefore(dayjs().startOf('day'))
          ? dayjs().startOf('day').toISOString()
          : onwardShareToExtend.dateFrom
        setValue(`assets.${index}.date`, {
          from: fromDate,
          to: onwardShareToExtend.dateTo,
        })
      } else if (!currentValue.date?.from) {
        // Only set default dates for new shares with no existing dates
        setValue(`assets.${index}.date`, {
          from: dayjs().startOf('day').toISOString(),
          to: dayjs().endOf('day').toISOString(),
        })
      }
    }

    clearErrors(`assets.${index}.date`)
  }

  return (
    <tr>
      <Td>
        {isLoading ? (
          <Input
            name={`assets.${index}.asset.placeholder`}
            disabled
            placeholder={formatMessage(messages.loading)}
          />
        ) : (
          <SelectField
            aria-labelledby={`assets.${index}.asset`}
            name={`assets.${index}.asset`}
            placeholder={formatMessage(messages.assetSelectPlaceholder)}
            leftAdorment={
              <SelectAdorment $disabled={!!shareToExtend}>{`#${index + 1}`}</SelectAdorment>
            }
            disabled={isSubmitting}
            displayInline
            required
            selectProps={{
              defaultValue,
              isDisabled: !!shareToExtend || !!onwardShareToExtend || isSubmitting,
              maxMenuHeight: 200,
              options: options,
              // @ts-expect-error Passing type info forward is not supported
              isOptionDisabled(
                option: SelectOption<
                  string | number,
                  { id: string; isOwnedFromOrganizationTo?: boolean; disabledReason?: string }
                >,
              ) {
                // Prevent selecting the same asset in multiple rows (allow current row's selection)
                if (
                  selectedAssets?.some(
                    (sa, i) => i !== index && String(sa.asset) === String(option.value),
                  )
                ) {
                  return true
                }

                if (option?.data?.isOwnedFromOrganizationTo) {
                  return true
                }

                // Offline blocks selection, unless location is intermittent
                return !!option?.data?.disabledReason
              },
            }}
          />
        )}
        {isCurrentAssetEasedOffline && (
          <div
            className={styles.offlineNotice}
            data-testid={`share-row-offline-notice-${index}`}
            role="status"
          >
            <LuInfo color={'#15A1CE'} size="14" strokeWidth={1.8} />
            <span>{OFFLINE_EASED_NOTICE}</span>
          </div>
        )}
        {/* Inline, like the reference and date errors — the form's summary also lists it. */}
        {errors?.assets?.[index]?.asset && (
          <Error data-testid={`share-row-asset-error-${index}`}>
            {(errors.assets[index]?.asset as FieldError).message}
          </Error>
        )}
      </Td>
      <Td>
        <Input
          name={`assets.${index}.reference`}
          placeholder="077/01"
          disabled={!!shareToExtend}
          maxLength={REFERENCE_MAX_LENGTH}
          value={currentValue.reference}
          style={{ minWidth: 140 }}
          onChange={(value) => {
            setValue(`assets.${index}.reference`, value.target.value)
          }}
          $hasError={!!errors?.assets?.[index]?.reference}
          data-testid={`share-row-reference-${index}`}
        />
        {errors?.assets?.[index]?.reference && (
          <Error>{(errors.assets[index]?.reference as FieldError).message}</Error>
        )}
      </Td>
      <DurationTypeTd>
        <Controller
          control={control}
          name={`assets.${index}.durationType`}
          render={({ field }) => (
            <RadioButtons
              name={`duration-type-${index}`}
              currentValue={durationType}
              onChange={(value) => {
                handleDurationTypeChange({
                  value,
                  field,
                  index,
                  shareToExtend,
                  onwardShareToExtend,
                  currentValue,
                  setValue,
                  clearErrors,
                  setDurationType,
                })
              }}
              options={durationOptions}
            />
          )}
        />
      </DurationTypeTd>
      <Td>
        <CellDuration>
          {currentValue?.durationType === 'custom' ? (
            <Controller
              control={control}
              name={`assets.${index}.date`}
              render={({ field }) => (
                <DatePicker
                  initialDate={{
                    from: onwardShareToExtend
                      ? dayjs(minDateRange).toISOString()
                      : field.value?.from,
                    to: field.value?.to,
                  }}
                  mode="range"
                  onChange={(value: DateRange) => {
                    field.onChange(value)
                    if (currentValue.durationType === 'custom') {
                      if (onwardShareToExtend) {
                        const dateRange = value as { from: Date; to: Date }
                        if (!dayjs(dateRange?.from).isSame(dayjs(), 'date')) {
                          onwardShareToExtend.dateFrom = dateRange?.from?.toISOString()
                        }
                        onwardShareToExtend.dateTo = dateRange?.to?.toISOString()
                      }
                    }
                  }}
                  placeholderFrom="Select Start"
                  placeholderTo="Select End"
                  hasError={!!errors?.assets?.[index]?.date}
                  disabled={[
                    {
                      before: minDateRange ? minDateRange : new Date(),
                      after: maxDateRange,
                    },
                  ]}
                  disabledRangeStart={shouldDisabledRangeStart()}
                />
              )}
            />
          ) : (
            <DatePickerTrigger disabled type="button">
              {dayjs().format(SHORT_DATE_FORMAT)}
            </DatePickerTrigger>
          )}
        </CellDuration>
        {errors?.assets?.[index]?.date && (
          <Error>{(errors.assets[index]?.date as FieldError).message}</Error>
        )}
      </Td>
      <Td $center>
        <Remove type="button" disabled={rows < 2} onClick={onRemove}>
          <LuCirclePlus size="20" />
        </Remove>
      </Td>
    </tr>
  )
}
